import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const SETTINGS_KEY = 'juzamma:reader:v1';
const ThemeContext = createContext(null);

function readStorage(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

export function useReaderSettings() {
  const defaults = { reciter: 'alafasy' };
  const [settings, setSettingsState] = useState(defaults);

  useEffect(() => {
    const stored = readStorage(SETTINGS_KEY, {});
    setSettingsState({ reciter: stored.reciter ?? defaults.reciter });
  }, []);

  const setSettings = useCallback((update) => {
    setSettingsState((current) => {
      const next = typeof update === 'function' ? update(current) : { ...current, ...update };
      window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return [settings, setSettings];
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState('light');

  useEffect(() => {
    const stored = window.localStorage.getItem('juzamma:theme');
    const preferred = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    setThemeState(stored === 'dark' || stored === 'light' ? stored : preferred);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const setTheme = useCallback((nextTheme) => {
    window.localStorage.setItem('juzamma:theme', nextTheme);
    setThemeState(nextTheme);
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme moet binnen ThemeProvider worden gebruikt');
  return value;
}
